/**
 * AP-35 Detailblatt 5 — **Stückliste und Beschaffung** (Baustein `PROJECT_MGMT.PROCUREMENT`).
 * Der Vertrag zwischen Backend, ERP und PWA.
 */

export const PROCUREMENT_STATUSES = ['OPEN', 'INQUIRED', 'ORDERED', 'CONFIRMED', 'RECEIVED', 'STOCK', 'PROVIDED', 'CANCELLED'] as const
export type ProcurementStatus = (typeof PROCUREMENT_STATUSES)[number]

export const PROCUREMENT_STATUS_LABELS: Readonly<Record<ProcurementStatus, string>> = {
  OPEN: 'offen',
  INQUIRED: 'angefragt',
  ORDERED: 'bestellt',
  // AP-39 11.4: Der Lieferant hat die Bestellzeile bestätigt (Auftragsbestätigung).
  CONFIRMED: 'bestätigt',
  RECEIVED: 'erhalten',
  STOCK: 'Lager',
  PROVIDED: 'beigestellt',
  CANCELLED: 'storniert',
}

/** Bestellt und noch nicht erhalten — „bestätigt" ist bestellt, nur mit Zusage des Lieferanten (AP-39 11.4). */
export const PROCUREMENT_AWAITING: readonly ProcurementStatus[] = ['ORDERED', 'CONFIRMED']
/** Ab der Bestellung gesperrt: Menge, Lieferant und Löschen, bis die Bestellung storniert ist. */
export const PROCUREMENT_LOCKED: readonly ProcurementStatus[] = ['ORDERED', 'CONFIRMED', 'RECEIVED']

export const PROCUREMENT_SOURCES = ['PURCHASE', 'STOCK', 'CUSTOMER_SUPPLIED'] as const
export type ProcurementSource = (typeof PROCUREMENT_SOURCES)[number]

export const PROCUREMENT_SOURCE_LABELS: Readonly<Record<ProcurementSource, string>> = {
  PURCHASE: 'Einkauf',
  STOCK: 'Lager',
  CUSTOMER_SUPPLIED: 'Beistellung',
}

/** Was Bedarf und Einkaufswert einer Stücklistenzeile brauchen. */
export interface BomZeilenPreis {
  quantity: number
  quantityBasis: 'TOTAL' | 'PER_UNIT'
  purchasePrice: number | null
  priceUnit: number
  discountPercent: number | null
}

/** Blatt 5 3. — Bedarf in der Zeileneinheit: „je Einheit" mal die Menge der Position. */
export function bomBedarf(z: Pick<BomZeilenPreis, 'quantity' | 'quantityBasis'>, positionsMenge: number): number {
  return Math.round(z.quantity * (z.quantityBasis === 'PER_UNIT' ? positionsMenge : 1) * 1000) / 1000
}

/**
 * Einkauf Soll der Zeile: Bedarf × Preis ÷ Preiseinheit × (1 − Rabatt), auf Cent — `null` ohne Preis.
 *
 * **Eine Formel für Server und Browser:** Die Stückliste rechnet damit den GP, während die Zeile getippt wird.
 */
export function bomEinkaufSoll(z: BomZeilenPreis, positionsMenge: number): number | null {
  if (z.purchasePrice === null) return null
  const rabatt = 1 - (z.discountPercent ?? 0) / 100
  return Math.round(((bomBedarf(z, positionsMenge) * z.purchasePrice * rabatt) / Math.max(1, z.priceUnit)) * 100) / 100
}

export const BOM_SOURCES = ['ARTICLE', 'FREE', 'CAD'] as const
export type BomSource = (typeof BOM_SOURCES)[number]

/** Blatt 13 3.1 `procurementApproval` — welche Freigabe das Einfügen in einen Beleg verlangt. */
export const PROCUREMENT_APPROVAL_MODES = ['NONE', 'ORDER_ONLY', 'INQUIRY_AND_ORDER'] as const
export type ProcurementApprovalMode = (typeof PROCUREMENT_APPROVAL_MODES)[number]

/** Blatt 5 6. — Zielfelder einer Import-Zuordnung. */
export const BOM_IMPORT_TARGETS = {
  name: 'Bezeichnung',
  quantity: 'Menge',
  unit: 'Einheit',
  material: 'Werkstoff',
  weightPerUnit: 'Gewicht je Einheit (kg)',
  totalWeight: 'Gewicht gesamt (kg)',
  articleNumber: 'Artikelnummer',
  category: 'Kategorie',
  grouping: 'Gruppierung',
  description: 'Langtext',
  altQuantity: 'Alternativmenge',
  altUnit: 'Alternativeinheit',
} as const
export type BomImportTarget = keyof typeof BOM_IMPORT_TARGETS

export const BOM_IMPORT_REQUIRED: readonly BomImportTarget[] = ['name', 'quantity']

/** Ergebnis je Zeile in der Vorschau des Imports (Blatt 5 6.). */
export type BomImportRowState = 'MATCHED' | 'FREE' | 'DUPLICATE' | 'INVALID'

/** Vorlage der Kategorien Metallbau (Blatt 5 2.3). */
export const BOM_CATEGORY_TEMPLATE: readonly { name: string; isAssemblyAccessory: boolean; countsWeight: boolean }[] = [
  { name: 'Stahl', isAssemblyAccessory: false, countsWeight: true },
  { name: 'Zubehör', isAssemblyAccessory: false, countsWeight: true },
  { name: 'Oberfläche', isAssemblyAccessory: false, countsWeight: false },
  { name: 'Fremdleistung', isAssemblyAccessory: false, countsWeight: false },
  { name: 'Montagezubehör', isAssemblyAccessory: true, countsWeight: true },
]

// ── Filter der Stückliste (AP-35 Nachtrag 6) ────────────────────────────────

/**
 * Die Felder, nach denen die Stückliste einer Position gefiltert wird. Oberfläche
 * und Export filtern mit **derselben** Funktion (`bomZeilePasst`) — sonst enthielte
 * die exportierte Datei andere Zeilen als die Liste, aus der sie kam.
 */
export const BOM_FILTER_KEYS = [
  'supplier',
  'order',
  'category',
  'source',
  'grouping',
  'status',
  'inquiryApproval',
  'orderApproval',
  'certificateMissing',
] as const
export type BomFilterKey = (typeof BOM_FILTER_KEYS)[number]

/** Wert für „ohne“ — kein Lieferant, keine Bestellung, keine Kategorie, keine Gruppierung. */
export const BOM_FILTER_NONE = '__none__'

/** Ein Filterfeld: die gewählten Werte, `not` kehrt es um („ist nicht“). Leer heißt: filtert nicht. */
export interface BomFilterValue {
  values: string[]
  not?: boolean
}
export type BomFilter = Partial<Record<BomFilterKey, BomFilterValue>>

/** Was der Filter an einer Zeile liest — die Ansicht der Stückliste erfüllt es. */
export interface BomFilterLine {
  supplier: { id: string } | null
  orderDocument: { id: string } | null
  category: { id: string } | null
  procurementSource: string
  grouping: string | null
  procurementStatus: string
  approvals: { inquiry: unknown | null; order: unknown | null }
  certificateState: string
}

/** Der Wert einer Zeile für ein Filterfeld. Ja/Nein-Felder liefern `yes` oder `no`. */
export function bomFilterWert(z: BomFilterLine, key: BomFilterKey): string {
  switch (key) {
    case 'supplier':
      return z.supplier?.id ?? BOM_FILTER_NONE
    case 'order':
      return z.orderDocument?.id ?? BOM_FILTER_NONE
    case 'category':
      return z.category?.id ?? BOM_FILTER_NONE
    case 'source':
      return z.procurementSource
    case 'grouping':
      return z.grouping?.trim() || BOM_FILTER_NONE
    case 'status':
      return z.procurementStatus
    case 'inquiryApproval':
      return z.approvals.inquiry ? 'yes' : 'no'
    case 'orderApproval':
      return z.approvals.order ? 'yes' : 'no'
    case 'certificateMissing':
      return z.certificateState === 'missing' || z.certificateState === 'insufficient' ? 'yes' : 'no'
  }
}

/** Alle gesetzten Felder müssen passen (UND); innerhalb eines Felds genügt ein Wert (ODER). */
export function bomZeilePasst(z: BomFilterLine, filter: BomFilter | null | undefined): boolean {
  if (!filter) return true
  for (const key of BOM_FILTER_KEYS) {
    const f = filter[key]
    if (!f || !f.values.length) continue
    const treffer = f.values.includes(bomFilterWert(z, key))
    if (f.not ? treffer : !treffer) return false
  }
  return true
}